import { createRouter, createWebHashHistory } from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw
} from 'vue-router'
import { isUrl } from '@/utils/is'
import { omit, cloneDeep } from 'lodash-es'

const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout'
      })
    })
}

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/'
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const meta = route.meta ?? {}
    // skip some route
    if (meta.hidden && !meta.canTo) {
      continue
    }

    let data: Nullable<AppRouteRecordRaw> = null

    let onlyOneChild: Nullable<string> = null
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(pathResolve(basePath, route.path), route.children[0].path)
      ) as string
    }

    // 开发者可以根据实际情况进行扩展
    for (const item of keys) {
      // 通过路径去匹配
      if (isUrl(item) && (onlyOneChild === item || route.path === item)) {
        data = Object.assign({}, route)
      } else {
        const routePath = (onlyOneChild ?? pathResolve(basePath, route.path)).trim()
        if (routePath === item || meta.followRoute === item) {
          data = Object.assign({}, route)
        }
      }
    }

    // recursive child routes
    if (route.children && data) {
      data.children = generateRoutesByFrontEnd(
        route.children,
        keys,
        pathResolve(basePath, data.path)
      )
    }
    if (data) {
      res.push(data as AppRouteRecordRaw)
    }
  }
  return res
}

// 后端控制路由生成
export const generateRoutesByServer = (routes: any[], parentPath = ''): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const menu of routes) {
    // 防御：后端 menus.route 偶尔会塞不带 / 前缀的值（早期 directory 节点遗留），
    // 直接给 Vue Router 注册会抛 "Route paths should start with a '/'" 整个应用挂掉
    // 兜底加 / 前缀，仅顶层（parentPath 空时）；子菜单走原 startsWith 逻辑转相对路径不动
    let absolutePath: string = String(menu.route ?? '')
    if (!parentPath && absolutePath && !absolutePath.startsWith('/')) {
      absolutePath = '/' + absolutePath
    }
    let path = absolutePath

    // 如果是子菜单，并且路径以父路径开头，则转换为相对路径
    if (parentPath && absolutePath.startsWith(parentPath)) {
      path = absolutePath.substring(parentPath.length).replace(/^\//, '')
      // 如果转换后为空（例如父子路径相同），则使用原始路径避免空路径路由
      if (path === '') path = absolutePath
    }

    // 1. 确定组件
    let component: any = Layout
    if (menu.type === 'menu') {
      const parts = absolutePath.split('/').filter(Boolean)
      const componentPath = parts
        .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('/')

      const directPath = `views/${componentPath}`
      const folderPath = `views/${componentPath}/${parts[parts.length - 1].charAt(0).toUpperCase() + parts[parts.length - 1].slice(1)}`

      if (modules[`../${directPath}.vue`] || modules[`../${directPath}.tsx`]) {
        component = modules[`../${directPath}.vue`] || modules[`../${directPath}.tsx`]
      } else if (modules[`../${folderPath}.vue`] || modules[`../${folderPath}.tsx`]) {
        component = modules[`../${folderPath}.vue`] || modules[`../${folderPath}.tsx`]
      } else {
        console.error(`未找到组件: ${directPath} 或 ${folderPath}`)
        component = Layout // 降级处理
      }
    }

    // 2. 构建路由对象
    const data: AppRouteRecordRaw = {
      path: path,
      name: (menu.name + menu.id).replace(/[^a-zA-Z0-9]/g, ''),
      component: component,
      meta: {
        title: menu.name,
        icon: menu.icon,
        hidden: menu.hidden === 1,
        alwaysShow: menu.type === 'directory'
      }
    }

    if (menu.redirect) {
      data.redirect = menu.redirect
    }

    // 3. 递归处理子路由，传入父级的绝对路径
    if (menu.children && menu.children.length > 0) {
      data.children = generateRoutesByServer(menu.children, absolutePath)
    }

    res.push(data)
  }
  return res
}

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  // 绝对路径应直接返回，避免在菜单重组/父子迁移后被错误拼接
  if (path.startsWith('/')) return path
  const childPath = path.startsWith('/') || !path ? path : `/${path}`
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim()
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    routeModule.children = routeModule.children || []
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule)
    }
  }
}
